import React from 'react';
import { Tooltip } from '@mantine/core';
import { observer } from 'mobx-react';
import UserProfileImage from 'components/user/user_profile_image';
import { AppUI } from '@seedlang/state';
import styled from '@emotion/styled';
import Text from 'components/text';
import Link from 'components/link';
import CommentCreate from 'components/comment/comment_create';
import NameWithIcons from 'components/user/name_with_icons';
import { pixify } from '@seedlang/utils';
import DeleteButton from 'components/button/delete_button';
import XpAnimation from 'components/xp_animation';
import { Constants, Theme, Layout } from '@seedlang/constants';
import { CommentShowWrapper } from '@seedlang/hoc';
import FeedbackList from 'components/feedback/feedback_list';
import { EyeIcon, PencilIcon, PinIcon } from '@seedlang/icons';
import TextDisplay from 'components/text_display';
import NameWithoutIcons from 'components/user/name_without_icons';
import ReactJson from 'react-json-view';
import { isPresent } from '@seedlang/utils/src';
import ReactTooltip from 'react-tooltip';
import CommentReaction, { AdminMenuItem, CommentActionButton } from 'components/comment/comment_reaction';

const BUILDER_DECK_TYPES = ['concept', 'triad', 'trivia'];
const TRAINER_DECK_TYPES = ['vocab', 'case', 'plural', 'synonym', 'gender', 'number', 'conjugation'];

const Wrapper = styled.div`
  position: relative;
  margin: ${props => props.margin};
  padding: ${props => props.padding};
  width: ${props => props.width};
  scroll-margin-top: ${pixify(Layout.DESKTOP_HEADER_HEIGHT)};
  a {
    text-decoration: underline;
  }
  ul {
    margin-left: 20px;
    li {
      list-style-type: disc;
    }
  }
  h1, h2, h3, h4, h5, h6 {
    margin: 10px 0;
  }
  .reply-icon, .smile-icon, .pencil-icon, .eye-icon, .pin-icon {
    fill: #333;
  }
  img {
    max-width: 500px;
    max-height: 500px;
  }
  .reply-icon {
    width: 12px;
    margin-right: 3px;
  }
  .smile-icon {
    width: 11px;
  }
  .eye-icon {
    width: 14px;
    height: 10px;
  }
  .pencil-icon {
    width: 10px;
  }
  .pin-icon {
    width: 11px;
    margin-right: 4px;
  }
  .pencil-icon, .smile-icon, .eye-icon {
    margin-right: 4px;
  }
  i {
    color: #333;
    font-size: 12px;
    margin: -1px 4px 0 0;
  }
`;

const IndentationWrapper = styled.div`
  margin: ${props => props.margin};
  border: ${props => props.border};
  position: relative;
  display: flex;
  padding: ${props => props.padding};
  flex-direction: column;
  width: ${props => props.isReply ? '75%' : 'auto'};
  border-radius: ${props => props.isReply ? '10px' : '5px'};
  background: ${props => props.background};
  @media only screen and (max-width : 649px) {
    margin-left: ${props => pixify(props.mobileOffset)};
    background: ${props => props.selected || props.unseen ? props.background : 'transparent'};
  }
  @media only screen and (min-width : 650px) and (hover: hover) and (pointer: fine) {
    &:hover .comment-reaction {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const ThreadLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${props => props.left};
  width: 1px;
  background: #dedede;
  @media only screen and (max-width : 649px) {
    display: ${props => props.hiddenOnMobile ? 'none' : 'block'};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  overflow: visible;
`;

const ProfileColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 20px 0 0;
`;

const TextWrapper = styled.div`
  position: relative;
  flex: 1;
  text-align: left;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  line-height: 18px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
`;

const PostedAt = styled.div`
  display: ${props => props.compactCommentLayout ? (props.mobileOnly ? 'block' : 'none') : (props.mobileOnly ? 'none' : 'block')};
  font-size: 11px;
  line-height: 16px;
  color: #9c9c9c;
  font-style: italic;
  margin-left: ${props => props.compactCommentLayout && props.mobileOnly ? 0 : '8px'};
  margin-top: ${props => props.compactCommentLayout && props.mobileOnly ? '-3px' : 0};
  @media only screen and (max-width : 649px) {
    display: ${props => props.mobileOnly ? 'block' : 'none'};
    margin: ${props => props.mobileOnly ? '-3px 0 0 0' : null};
  }
`;

const BottomBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0;
`;

const HiddenLabel = styled.div`
  background: ${Theme.orange};
  color: white;
  font-size: 11px;
  line-height: 14px;
  margin-top: 6px;
  padding: 2px 7px;
  border-radius: 5px;
`;

const HiddenLabelTooltipContent = styled.div`
  text-align: right;
`;

const DebugInfoWrapper = styled.div`
  max-height: 500px;
  overflow: auto;
  border: 1px solid #9c9c9c;
  border-radius: 5px;
  padding: 10px;
  margin-top: 10px;
`;

const DebugInfoHeader = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 15px;
  row-gap: 8px;
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid #ddd;
`;

const DebugInfoRow = styled.div`
  display: contents;
`;

const DebugInfoLabel = styled.div`
  color: #777;
  font-size: 12px;
  font-weight: bold;
`;

const DebugInfoValue = styled.div`
  font-size: 14px;
`;

const PlatformDebugInfoValue = styled(DebugInfoValue)`
  display: flex;
  align-items: center;
`;

const PlatformIcon = styled.i`
  && {
    width: 16px;
    margin: 0 7px 0 0;
    color: #333;
    font-size: 16px;
    line-height: 1;
    text-align: center;
  }
`;

@observer
class CommentShow extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showCopyConfirmation: false,
    };
  }

  handleCopyLink = () => {
    const commentId = this.props.comment.id;
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}#comment-${commentId}`;
    navigator.clipboard.writeText(url).then(() => {
      this.setState({ showCopyConfirmation: true });
      setTimeout(() => {
        this.setState({ showCopyConfirmation: false });
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy link: ', err);
    });
  };

  get commentLayout() {
    if (this.props.commentLayout) {
      return this.props.commentLayout;
    } else if (this.props.compactCommentLayout) {
      return 'compact';
    } else if (this.props.alignWithForumPost) {
      return 'forum';
    }
    return 'embedded';
  }

  get commentPaddingTop() {
    return String(pixify(this.props.padding)).split(' ')[0];
  }

  get isReply() {
    return this.props.comment.depth > 0;
  }

  get linkToAdminUser() {
    return this.props.hideUserProfile && AppUI.user.anyAdmin && this.props.comment.user?.id;
  }

  goToAdminUser = () => {
    AppUI.routeStore.routeToNamed('creator.users.edit', {userId: this.props.comment.user.id});
  };

  get outerPadding() {
    if (this.isReply) {
      return '6px 0';
    }
    if (this.commentLayout === 'forum') {
      return '10px 0 10px 0';
    }
    return `calc(${this.commentPaddingTop} - 10px) 0 10px 0`;
  }

  get innerPadding() {
    if (this.isReply) {
      return '18px 14px 13px 14px';
    }
    return this.commentLayout === 'embedded' ? '10px 20px 10px 20px' : '10px 0 0 0';
  }

  get alignActionsWithContent() {
    return this.commentLayout === 'forum';
  }

  get threadDepth() {
    return Math.min(this.props.compactCommentLayout ? 1 : 4, this.props.comment.depth || 0);
  }

  get mobileOffset() {
    const depth = Math.min(1, this.props.comment.depth || 0);
    return depth * this.props.indentation;
  }

  get commentContentOffset() {
    return pixify(this.props.userProfileImageSize + 20);
  }

  get reactionBarOffset() {
    return pixify(this.props.userProfileImageSize + 25);
  }

  get commentBackground() {
    if (this.showSeenStatus && this.props.comment.seenByAdmin === false) {
      return '#fdfae2';
    }
    if (this.props.selected) {
      return Theme.lightBlue;
    }
    return this.isReply ? Theme.softButtonBackground : '#fff';
  }

  get showSeenStatus() {
    return AppUI.siteIsSeedlang && (AppUI.user.admin || AppUI.user.moderator) && this.props.commentableType === 'ForumThread' && !this.props.comment.deleted;
  }

  get debugInfo() {
    return this.props.comment.debugInfoJson;
  }

  get debugDeckInfo() {
    return this.debugInfo?.['deck_info'] || {};
  }

  get debugUserInfo() {
    return this.debugInfo?.['user_info'] || {};
  }

  get debugDeckName() {
    const deckType = this.debugDeckInfo['deck_type'];
    const deckName = this.debugDeckInfo['deck_name'];
    if (deckType === 'review') {
      return 'Review deck';
    }
    if (TRAINER_DECK_TYPES.includes(deckType)) {
      return `${deckType.charAt(0).toUpperCase()}${deckType.slice(1)} Trainer`;
    }
    return deckName && deckName.toLowerCase() !== 'unknown' ? deckName : deckType || 'Unknown';
  }

  get debugDeckHasBuilderLink() {
    return BUILDER_DECK_TYPES.includes(this.debugDeckInfo['deck_type']) && this.debugDeckInfo['deck_id'];
  }

  get debugCardTypeSlug() {
    return this.debugInfo?.['card']?.['card_type_slug'] || this.debugInfo?.['animation_cards']?.[0]?.['card_type'];
  }

  get debugCardId() {
    return this.debugInfo?.['card']?.['id'] || this.debugInfo?.['animation_cards']?.[0]?.['card_id'];
  }

  get debugCardType() {
    if (!this.debugCardTypeSlug) {
      return 'Unknown';
    }
    const cardTypeName = Constants.REVIEW_CARD_TYPES.find(item => item.slug === this.debugCardTypeSlug)?.name || 'Unknown';
    return cardTypeName;
  }

  get debugPlatform() {
    if (this.debugUserInfo['is_native'] === '✓') {
      if (this.debugUserInfo['device_os'] === 'ios') {
        return 'iOS';
      }
      if (this.debugUserInfo['device_os'] === 'android') {
        return 'Android';
      }
    }
    return this.debugUserInfo['viewport_width'] < 700 ? 'Mobile web' : 'Web';
  }

  renderThreadLines() {
    if (this.threadDepth === 0) {
      return null;
    }

    return Array.from({length: this.threadDepth}, (_item, index) => (
      <ThreadLine
        key={index}
        left={pixify((index + 0.5) * this.props.indentation)}
        hidden={this.props.compactCommentLayout && index > 0}
        hiddenOnMobile={index > 0}
      />
    ));
  }

  renderPostedAt(mobileOnly = false) {
    return (
      <PostedAt
        mobileOnly={mobileOnly}
        compactCommentLayout={this.props.compactCommentLayout}
      >
        {this.props.comment.createdAt.relativeTime}
        {
          this.props.comment.updated &&
            <span
              style={{marginLeft: 5}}
            >
              (updated)
            </span>
        }
      </PostedAt>
    );
  }

  renderHiddenLabel() {
    if (!this.props.comment.hidden || (!AppUI.user.admin && !AppUI.user.moderator)) {
      return null;
    }

    return (
      <>
        <HiddenLabel
          data-tip
          data-for={`hidden-comment-label-${this.props.comment.id}`}
          data-place='right'
        >
          Hidden
        </HiddenLabel>
        <ReactTooltip
          type='dark'
          effect='solid'
          place='right'
          id={`hidden-comment-label-${this.props.comment.id}`}
          className='custom-tooltip'
        >
          <HiddenLabelTooltipContent>
            This post is hidden except to admins and the poster
          </HiddenLabelTooltipContent>
        </ReactTooltip>
      </>
    );
  }

  get hiddenCommentVisibilityText() {
    const { comment } = this.props;
    const actionText = comment.hidden ? 'Remains' : 'Will still be';
    const willBeText = comment.hidden ? 'Is' : 'Will be';

    const visibleTo = ['moderators'];

    if (comment.user && !comment.user.moderator && !comment.user.anyAdmin) {
      visibleTo.push(comment.user.name);
    }

    if (comment.respondToUser && !comment.respondToUser.moderator && !comment.respondToUser.anyAdmin && (comment.user?.moderator || comment.user?.anyAdmin)) {
      visibleTo.push(comment.respondToUser.name);
    }

    const visibilityList = visibleTo.length > 1
      ? `${visibleTo.slice(0, -1).join(', ')} and ${visibleTo.slice(-1)[0]}`
      : visibleTo.join(', ');

    if (isPresent(comment.respondToUser)) {
      return `${actionText} visible to ${visibilityList}`;
    } else {
      return `${willBeText} visible to ${visibilityList}`;
    }
  }

  render() {
    return (
      <Wrapper
        margin={this.props.margin}
        padding={this.outerPadding}
        id={`comment-${this.props.comment.id}`}
      >
        {
          AppUI.siteIsSeedlang &&
            <XpAnimation
              namespace={this.props.comment.id}
              position='absolute'
              bottom='0'
              margin={`0 0 0 ${pixify(this.props.offset)}`}
            />
        }
        {this.renderThreadLines()}
        <IndentationWrapper
          margin={`0 0 0 ${pixify(this.props.offset)}`}
          border={this.props.border}
          background={this.commentBackground}
          isReply={this.isReply}
          mobileOffset={this.mobileOffset}
          selected={this.props.selected}
          unseen={this.showSeenStatus && this.props.comment.seenByAdmin === false}
          padding={this.innerPadding}
          onMouseLeave={this.props.onCloseFeedback}
        >
            <Content>
              <ProfileColumn>
                <UserProfileImage
                  clickToProfile={!this.props.hideUserProfile}
                  onClick={this.linkToAdminUser ? this.goToAdminUser : null}
                  hideProStatus={this.props.hideUserProfile}
                  fontSize='13px'
                  width={this.props.userProfileImageSize}
                  margin='0'
                  user={this.props.comment.user}
                />
                {this.renderHiddenLabel()}
              </ProfileColumn>
              <TextWrapper>
                <Info>
                  <TopBar>
                    {
                      !this.props.hideUserProfile &&
                        <NameWithIcons
                          user={this.props.comment.user}
                          hideIcons={this.props.compactCommentLayout}
                        />
                    }
                    {
                      this.props.hideUserProfile &&
                        (
                          this.linkToAdminUser ?
                            <Link
                              to={{name: 'creator.users.edit', params: {userId: this.props.comment.user.id}}}
                            >
                              <NameWithoutIcons
                                user={this.props.comment.user}
                              />
                            </Link>
                            :
                            <NameWithoutIcons
                              user={this.props.comment.user}
                            />
                        )
                    }
                  </TopBar>
                  {this.renderPostedAt()}
                </Info>
                {
                  this.props.comment.text && !this.props.comment.deleted &&
                    <Text
                      fontSize={this.props.fontSize}
                      lineHeight={this.props.lineHeight}
                    >
                      {
                        !this.props.comment.deleted &&
                          <TextDisplay
                            markdown={this.props.comment.text}
                            preserveParagraphLineBreaks
                          />
                      }
                    </Text>
                }
                {
                  this.props.comment.deleted &&
                    <Text
                      fontSize={this.props.fontSize}
                      lineHeight={this.props.lineHeight}
                      color='#B1B1B1'
                      margin='10px 0'
                    >
                      [Deleted]
                    </Text>
                }
                {this.renderPostedAt(true)}
              </TextWrapper>
            </Content>
            <BottomBar>
              <CommentReaction
                backgroundOnHover={AppUI.siteIsSeedlang ? Theme.darkestBlue : 'gray'}
                topRight
                topRightRight={this.alignActionsWithContent ? '0' : undefined}
                showReply={AppUI.userIsLoaded}
                showFeedback={AppUI.userIsLoaded}
                onReply={this.props.onToggleCreate}
                onToggleFeedback={this.props.onToggleFeedback}
                expandFeedback={this.props.expandFeedback}
                onClickFeedback={this.props.onClickFeedback}
                adminItems={[
                  !this.props.comment.deleted && (this.props.comment.user.id === AppUI.user.id || AppUI.user.admin) &&
                    <AdminMenuItem
                      key='edit'
                      onClick={this.props.onToggleEdit}
                    >
                      <PencilIcon />
                      Edit
                    </AdminMenuItem>,
                  !this.props.comment.deleted && (this.props.comment.user.id === AppUI.user.id || AppUI.user.admin) &&
                    <DeleteButton
                      key='delete'
                      allowDeletion
                      onConfirm={this.props.onDeleteComment}
                      message='Delete this comment?'
                      confirmationMessageFloatsRight
                      right='auto'
                      left='10px'
                      top='-54px'
                    >
                      <AdminMenuItem>
                        <i className='fa fa-times' />
                        Delete
                      </AdminMenuItem>
                    </DeleteButton>,
                  (AppUI.user.admin || AppUI.user.moderator) &&
                    <AdminMenuItem
                      key='pin'
                      onClick={this.props.onTogglePinned}
                    >
                      <PinIcon />
                      {this.props.comment.pinned ? 'Unpin' : 'Pin'}
                    </AdminMenuItem>,
                  (AppUI.user.admin || AppUI.user.moderator) &&
                    <React.Fragment key='hide'>
                      <AdminMenuItem
                        data-tip
                        data-for={`hidden-comment-${this.props.comment.id}`}
                        onClick={this.props.onToggleHidden}
                      >
                        <EyeIcon />
                        {this.props.comment.hidden ? 'Show' : 'Hide'}
                      </AdminMenuItem>
                      <ReactTooltip
                        type='dark'
                        effect='solid'
                        id={`hidden-comment-${this.props.comment.id}`}
                        className='custom-tooltip'
                      >
                        {this.hiddenCommentVisibilityText}
                      </ReactTooltip>
                    </React.Fragment>,
                  (AppUI.user.admin || AppUI.user.moderator) && !this.props.comment.user.admin &&
                    <AdminMenuItem
                      key='auto-hide'
                      onClick={this.props.onToggleAutoHide}
                    >
                      <EyeIcon />
                      {this.props.comment.user.autoHideComments ? 'Auto-Hide: On' : 'Auto-Hide: Off'}
                    </AdminMenuItem>,
                  AppUI.user.admin && isPresent(this.props.comment.debugInfo) &&
                    <AdminMenuItem
                      key='debug-info'
                      onClick={this.props.onToggleDebugInfo}
                    >
                      <i className='fa fa-code' />
                      {this.props.expandDebugInfo ? 'Hide Debug' : 'Show Debug'}
                    </AdminMenuItem>,
                  (AppUI.user.admin || AppUI.user.moderator) &&
                    <AdminMenuItem
                      key='copy-link'
                      onClick={this.handleCopyLink}
                    >
                      <i className='fa fa-link' />
                      {this.state.showCopyConfirmation ? 'Copied!' : 'Copy Link'}
                    </AdminMenuItem>,
                ]}
              >
                {this.showSeenStatus && (
                  <Tooltip label={this.props.comment.seenByAdmin ? 'Mark as Unchecked' : 'Mark as Checked'} withArrow>
                    <CommentActionButton
                      aria-label={this.props.comment.seenByAdmin ? 'Mark as Unchecked' : 'Mark as Checked'}
                      disabled={this.props.updatingSeenByAdmin}
                      onClick={this.props.onToggleSeenByAdmin}
                      type='button'
                    >
                      <i
                        aria-hidden
                        className='fa fa-check'
                        style={{fontSize: '16px', margin: 0, opacity: this.props.comment.seenByAdmin ? 1 : 0.3}}
                      />
                    </CommentActionButton>
                  </Tooltip>
                )}
              </CommentReaction>
            </BottomBar>
            {
              this.props.comment.feedbacks && this.props.comment.feedbacks.length > 0 &&
                <FeedbackList
                  id={this.props.comment.id}
                  feedbacks={this.props.comment.feedbacks}
                  commentId={this.props.comment.id}
                  marginLeft={this.reactionBarOffset}
                  marginTop='0'
                  onChange={this.props.onChange}
                />
            }
            {
              (this.props.expandCreate || this.props.expandEdit) &&
                <CommentCreate
                  namespace={this.props.comment.id}
                  background='#fff'
                  textInputButtonBackground='#FFF'
                  edit={this.props.expandEdit}
                  text={this.props.expandEdit ? this.props.comment.text : null}
                  suggestedText={this.props.expandEdit ? null : this.props.comment.aiSuggestedAnswer}
                  commentable={this.props.commentable}
                  commentableType={this.props.commentableType}
                  commentableId={this.props.commentableId}
                  parentId={this.props.comment.id}
                  afterCreateComment={this.props.onChange}
                  afterSubmitComment={this.props.afterSubmitComment}
                  margin={`10px 0 10px ${this.commentContentOffset}`}
                  languageId={this.props.languageId || 'DE'}
                  hideSpecialCharacters={this.props.hideSpecialCharacters}
                />
            }
            {
              this.props.expandDebugInfo &&
              <DebugInfoWrapper>
                <DebugInfoHeader>
                  <DebugInfoRow>
                    <DebugInfoLabel>Deck</DebugInfoLabel>
                    <DebugInfoValue>
                      {
                        this.debugDeckHasBuilderLink ?
                          <Link
                            target='_blank'
                            rel='noopener noreferrer'
                            to={{
                              name: 'builder.decks.edit',
                              params: { deckId: this.debugDeckInfo['deck_id'] },
                              query: { cardId: this.debugCardId },
                            }}
                          >
                            {this.debugDeckName}
                          </Link>
                          :
                          this.debugDeckName
                      }
                    </DebugInfoValue>
                  </DebugInfoRow>
                  <DebugInfoRow>
                    <DebugInfoLabel>Card type</DebugInfoLabel>
                    <DebugInfoValue>{this.debugCardType}</DebugInfoValue>
                  </DebugInfoRow>
                  <DebugInfoRow>
                    <DebugInfoLabel>Platform</DebugInfoLabel>
                    <PlatformDebugInfoValue>
                      <PlatformIcon className={`fa fa-${this.debugPlatform === 'Web' ? 'desktop' : 'mobile'}`} />
                      {this.debugPlatform}
                    </PlatformDebugInfoValue>
                  </DebugInfoRow>
                </DebugInfoHeader>
                <ReactJson
                  src={this.debugInfo}
                  displayDataTypes={false}
                  displayObjectSize={false}
                />
              </DebugInfoWrapper>
            }
          </IndentationWrapper>
      </Wrapper>
    );
  }
}

export default CommentShowWrapper(CommentShow);
